import React from 'react';
import { CheckCircle2 } from 'lucide-react';

export default function AboutSection() {
    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 20px',
        },
        section1: {
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            padding: '60px 0',
            position: 'relative',
            overflow: 'hidden',
            '@media (min-width: 768px)': {
                padding: '120px 0',
            }
        },
        section1Decoration: {
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '500px',
            height: '500px',
            background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 1,
        },
        contentWrapper: {
            position: 'relative',
            zIndex: 2,
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, rgb(10 225 52) 0%, rgb(139, 92, 246) 100%)',
            color: 'white',
            borderRadius: '50px',
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '24px',
            animation: 'fadeInDown 0.8s ease-out',
        },
        mainTitle: {
            fontSize: '32px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '20px',
            lineHeight: '1.2',
            animation: 'fadeInUp 0.8s ease-out 0.2s both',
            '@media (min-width: 768px)': {
                fontSize: '48px',
            }
        },
        subtitle: {
            fontSize: '16px',
            color: '#64748b',
            lineHeight: '1.6',
            maxWidth: '600px',
            margin: '0 auto',
            animation: 'fadeInUp 0.8s ease-out 0.4s both',
            '@media (min-width: 768px)': {
                fontSize: '18px',
            }
        },
        section2: {
            padding: '60px 0',
            background: 'white',
            position: 'relative',
            '@media (min-width: 768px)': {
                padding: '100px 0',
            }
        },
        section2Decoration: {
            position: 'absolute',
            bottom: '-150px',
            left: '-150px',
            width: '400px',
            height: '400px',
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.06) 0%, transparent 70%)',
            borderRadius: '50%',
            zIndex: 0,
        },
        imageWrapper: {
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            animation: 'slideInLeft 0.8s ease-out',
            marginBottom: '40px',
            '@media (min-width: 768px)': {
                marginBottom: '0',
            }
        },
        image: {
            width: '100%',
            height: 'auto',
            display: 'block',
        },
        contentBox: {
            animation: 'slideInRight 0.8s ease-out',
        },
        questionText: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '24px',
            lineHeight: '1.3',
            '@media (min-width: 768px)': {
                fontSize: '42px',
            }
        },
        highlightText: {
            background: 'linear-gradient(135deg, rgb(24 181 33) 0%, rgb(139, 92, 246) 100%) text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            textDecoration: 'underline',
            textDecorationColor: '#6366f1',
            textDecorationThickness: '3px',
            textUnderlineOffset: '8px',
        },
        paragraph: {
            fontSize: '14px',
            color: '#475569',
            lineHeight: '1.8',
            marginBottom: '24px',
            textAlign: 'justify',
            '@media (min-width: 768px)': {
                fontSize: '16px',
            }
        },
        paragraphFirst: {
            fontSize: '16px',
            fontWeight: '500',
            color: '#1e293b',
            marginBottom: '24px',
            '@media (min-width: 768px)': {
                fontSize: '17px',
            }
        },
        featureItem: {
            display: 'flex',
            gap: '12px',
            marginTop: '32px',
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            borderRadius: '12px',
            borderLeft: '4px solid ',
        },
        checkIcon: {
            color: '#2c3e50',
            flexShrink: 0,
            marginTop: '2px',
        },
    };

    const keyframes = `
    @keyframes fadeInDown {
      from {
        opacity: 0;
        transform: translateY(-20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes fadeInUp {
      from {
        opacity: 0;
        transform: translateY(20px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes slideInLeft {
      from {
        opacity: 0;
        transform: translateX(-40px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }
    @keyframes slideInRight {
      from {
        opacity: 0;
        transform: translateX(40px);
      }
      to {
        opacity: 1;
        transform: translateX(0);
      }
    }

    @media (max-width: 767px) {
      .grid-responsive {
        display: flex !important;
        flex-direction: column;
        gap: 0 !important;
      }
    }

    @media (min-width: 768px) {
      .grid-responsive {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 60px !important;
        align-items: center !important;
      }
    }
  `;

    return (
        <>
            <style>{keyframes}</style>

            {/* Section 1: Header/Introduction */}
            <section style={styles.section1}>
                <div style={styles.section1Decoration}></div>
                <div style={{ ...styles.container, ...styles.contentWrapper }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={styles.badge}>
                            <span>✨ NOTRE PROCESSUS</span>
                        </div>
                        <h2 style={styles.mainTitle}>
                            Plus qu'une plateforme, <br/>une solution pratique pour la vie de tous les jours
                        </h2>
                        <p style={styles.subtitle}>
                            Découvrez comment Driven Home Services simplifie votre quotidien en vous connectant avec les meilleurs prestataires au Cameroun
                        </p>
                    </div>
                </div>
            </section>

            {/* Section 2: About Content */}
            <section style={styles.section2}>
                <div style={styles.section2Decoration}></div>
                <div style={styles.container}>
                    <div className="grid-responsive" style={{ position: 'relative', zIndex: 2 }}>
                        {/* Image */}
                        <div style={styles.imageWrapper}>
                            <img
                                src="assets/images/element/img.jpg"
                                alt="Utilisatrice de la plateforme Driven Home Services Cameroun"
                                style={styles.image}
                            />
                        </div>

                        {/* Content */}
                        <div style={styles.contentBox}>
                            <h3 style={styles.questionText}>
                                La plateforme fonctionne-t-elle vraiment ? <br/>
                                <span style={styles.highlightText}>Oui, et de manière très efficace.</span>
                            </h3>

                            <p style={styles.paragraphFirst}>
                                Vous vous demandez peut-être si Driven Home Services peut vraiment vous simplifier la vie au Cameroun ?
                                La réponse est claire : tout à fait.
                            </p>

                            <p style={styles.paragraph}>
                                <strong>Prenons un exemple concret :</strong> Une maman de trois enfants à Douala a trouvé en
                                quelques jours seulement une nounou qui parlait anglais avec ses enfants, préparait des repas
                                équilibrés et comprenait les bases de l'éducation. Le tout, sans passer des heures au téléphone
                                ou échanger d'innombrables messages.
                            </p>

                            <p style={styles.paragraph}>
                                Elle a simplement créé un compte, renseigné ses besoins, et identifié la candidate idéale.
                                En peu de temps, un défi de garde d'enfants a été résolu rapidement, efficacement,
                                et en toute tranquillité d'esprit.
                            </p>

                            <p style={styles.paragraph}>
                                <strong>C'est cela la promesse de Driven Home Services Cameroun :</strong> une plateforme conçue pour vous faire
                                gagner du temps, soulager votre charge mentale et trouver la perle rare en toute confiance.
                            </p>

                            {/* Feature highlights */}
                            <div style={styles.featureItem}>
                                <CheckCircle2 size={24} style={styles.checkIcon} />
                                <div>
                                    <strong style={{ color: '#1e293b' }}>Gain de temps</strong>
                                    <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
                                        Trouvez le professionnel idéal en quelques minutes
                                    </p>
                                </div>
                            </div>

                            <div style={styles.featureItem}>
                                <CheckCircle2 size={24} style={styles.checkIcon} />
                                <div>
                                    <strong style={{ color: '#1e293b' }}>Confiance totale</strong>
                                    <p style={{ fontSize: '14px', color: '#64748b', margin: '4px 0 0 0' }}>
                                        Profils vérifiés et évaluations transparentes
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}